import React, { useState, useEffect } from 'react';

// --- Helper & UI Components ---

const LoadingSpinner = () => (
  <div className="flex justify-center items-center p-20">
    <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-gray-900"></div>
  </div>
);

// --- Main Application Components ---

const Header = ({ logoUrl }) => (
  <header className="bg-white shadow-sm p-4 sticky top-0 z-20">
    <div className="container mx-auto flex justify-between items-center">
      <h1 className="text-2xl font-bold text-gray-800">VML Toolbox</h1>
      <img src={logoUrl} alt="VML Logo" className="h-8 w-auto" />
    </div>
  </header>
);

const ToolCard = ({ tool }) => {
  // Correctly handle the logo URL, which is a direct string from Airtable.
  const logoUrl = tool['tool-logo'];

  return (
    <a
      href={tool['tool-link']}
      target="_blank"
      rel="noopener noreferrer"
      className="block bg-white rounded-xl shadow-md p-6 transition-transform transform hover:scale-105 hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-opacity-50"
    >
      <div className="flex flex-col h-full">
        <div className="flex-shrink-0 mb-4 h-16 flex items-center justify-center">
          {logoUrl && typeof logoUrl === 'string' ? (
            <img src={logoUrl} alt={`${tool.tool_name} logo`} className="max-h-full max-w-full w-auto" />
          ) : (
            <div className="h-16 w-full bg-gray-200 rounded-md flex items-center justify-center">
              <span className="text-gray-500 text-sm">No Logo</span>
            </div>
          )}
        </div>
        <div className="flex-grow flex flex-col text-center">
          <h3 className="text-xl font-bold text-gray-900 mb-2">{tool.tool_name || 'Unnamed Tool'}</h3>
          <p className="text-gray-600 text-sm flex-grow">{tool.tool_description || 'No description available.'}</p>
        </div>
      </div>
    </a>
  );
};

const ToolGrid = ({ tools }) => {
  if (tools.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-md p-6 mb-6">
        <p className="text-center text-gray-500">No tools found.</p>
      </div>
    );
  }

  // Updated grid to show 2 columns on medium screens and up
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-2 gap-6">
      {tools.map(tool => (
        <ToolCard key={tool.id} tool={tool} />
      ))}
    </div>
  );
};


// --- Main App Component ---

function App() {
  // State
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const VML_LOGO_URL = "https://i.ibb.co/1wxvDjJ/BLACK-Logo-Snowflake-VML.png";

  // Data Fetching
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setError(null);
      try {
        // Fetching from the new 'hotlist' table via our API
        const res = await fetch(`/api/airtable?table=hotlist`);
        if (!res.ok) {
          const errData = await res.json();
          throw new Error(errData.error || 'Failed to fetch data from Airtable.');
        }
        const data = await res.json();
        const records = (data.records || []).map(rec => ({ id: rec.id, ...rec.fields }));

        setTools(records);

      } catch (err) {
        setError(err.message);
        console.error("Error loading data:", err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  return (
    <div className="bg-gray-100 min-h-screen font-sans">
      <Header logoUrl={VML_LOGO_URL} />
      <main className="container mx-auto p-6">
        {loading && <LoadingSpinner />}
        {error && (
          <div className="text-center text-red-600 bg-red-100 p-6 rounded-md shadow-md">
            <h2 className="text-xl font-bold mb-2">Error Loading Dashboard</h2>
            <pre className="text-sm text-left whitespace-pre-wrap">{error}</pre>
          </div>
        )}
        {!loading && !error && (
          <ToolGrid tools={tools} />
        )}
      </main>
    </div>
  );
}

export default App;
